/**
 * Staff Mistakes Report Tab
 * Admin component to view and manage staff mistake incidents.
 * 
 * Features:
 * - Summary statistics (total incidents, losses, staff charges)
 * - Pending approval queue for managers
 * - Per-staff breakdown for performance reviews
 * - By mistake type analysis
 * - Date filtering
 * - Financial reporting
 */
import React, { useState, useCallback, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { 
  AlertTriangle, RefreshCw, DollarSign, Users, Package, Calendar,
  TrendingDown, CheckCircle, XCircle, Clock, ChevronDown, ChevronUp,
  User, FileText, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '@/utils/apiClient';

export default function MistakeLossReportTab() {
  const [report, setReport] = useState(null);
  const [pendingMistakes, setPendingMistakes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [period, setPeriod] = useState('month');
  const [expandedSection, setExpandedSection] = useState('pending');
  const [approvingId, setApprovingId] = useState(null);

  // Fetch summary report
  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ period });
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);

      const res = await api.get(`/mistakes/report/summary?${params.toString()}`);
      setReport(res.data);
    } catch (err) {
      toast.error('Error fetching report');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, period]);

  // Fetch pending mistakes
  const fetchPending = useCallback(async () => {
    try {
      const res = await api.get('/mistakes/list?status=pending_approval&limit=50');
      setPendingMistakes(res.data.mistakes || []);
    } catch (err) {
      console.error('Error fetching pending mistakes:', err);
    }
  }, []);

  useEffect(() => {
    fetchReport();
    fetchPending();
  }, [fetchReport, fetchPending]);

  // Approve or reject mistake
  const handleApproval = async (mistakeId, approved, notes = '') => {
    setApprovingId(mistakeId);
    try {
      // Optionally add staff ID if POS session exists
      const posSession = localStorage.getItem('pos_session');
      let staffId = null;
      if (posSession) {
        try {
          const parsed = JSON.parse(posSession);
          staffId = parsed.staff_id;
        } catch (e) {
          // Invalid JSON, ignore
        }
      }
      
      const config = staffId ? { headers: { 'X-Staff-ID': staffId } } : {};
      const res = await api.post(`/mistakes/${mistakeId}/approve`, { approved, manager_notes: notes }, config);
      
      toast.success(res.data.message);
      fetchPending();
      fetchReport();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error processing approval');
    } finally {
      setApprovingId(null);
    }
  };

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('en-FJ', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    }).format(val || 0);
  };

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-FJ', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <label className="text-sm text-muted-foreground">Period</label>
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="block w-32 p-2 border rounded-lg bg-background"
              >
                <option value="week">Week</option>
                <option value="month">Month</option>
                <option value="quarter">Quarter</option>
                <option value="year">Year</option>
              </select>
            </div>
            <div>
              <label className="text-sm text-muted-foreground">Start Date</label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-40"
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground">End Date</label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-40"
              />
            </div>
            <Button onClick={() => { fetchReport(); fetchPending(); }} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {report && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 dark:bg-amber-800 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Incidents</p>
                  <p className="text-2xl font-bold">{report.totals.total_incidents}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 dark:bg-red-800 rounded-lg">
                  <TrendingDown className="w-5 h-5 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Business Loss</p>
                  <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                    ${formatCurrency(report.totals.total_business_loss)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 dark:bg-green-800 rounded-lg">
                  <DollarSign className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Staff Charged</p>
                  <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                    ${formatCurrency(report.totals.total_staff_charged)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-800 rounded-lg">
                  <Package className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Items Value</p>
                  <p className="text-2xl font-bold">${formatCurrency(report.totals.total_items_value)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 dark:bg-purple-800 rounded-lg">
                  <Clock className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Pending Approval</p>
                  <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                    {report.totals.pending_approval}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Pending Approvals */}
      <Card>
        <CardHeader 
          className="cursor-pointer" 
          onClick={() => toggleSection('pending')}
        >
          <CardTitle className="text-lg flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-purple-500" />
              Pending Manager Approval
              {pendingMistakes.length > 0 && (
                <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 text-sm rounded-full">
                  {pendingMistakes.length}
                </span>
              )}
            </span>
            {expandedSection === 'pending' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </CardTitle>
        </CardHeader>
        <AnimatePresence>
          {expandedSection === 'pending' && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
            >
              <CardContent>
                {pendingMistakes.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No pending approvals</p>
                ) : (
                  <div className="space-y-3">
                    {pendingMistakes.map((mistake) => (
                      <div key={mistake.id} className="p-4 bg-muted rounded-lg">
                        <div className="flex flex-wrap justify-between items-start gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="font-mono text-sm text-muted-foreground">{mistake.incident_id}</span>
                              <span className={`px-2 py-0.5 text-xs rounded ${
                                mistake.severity === 'high' ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' :
                                mistake.severity === 'medium' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300' :
                                'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                              }`}>
                                {mistake.severity}
                              </span>
                            </div>
                            <p className="font-medium text-foreground">{mistake.staff_name} ({mistake.staff_role})</p>
                            <p className="text-sm text-muted-foreground">{mistake.mistake_type_name}</p>
                            <p className="text-sm mt-1">{mistake.description}</p>
                            <div className="flex flex-wrap gap-4 mt-2 text-xs text-muted-foreground">
                              <span>Items: ${formatCurrency(mistake.items_total)}</span>
                              <span className="text-amber-600 dark:text-amber-400 font-medium">
                                Staff Charge: ${formatCurrency(mistake.staff_charge_amount)}
                              </span>
                              <span>Logged by: {mistake.logged_by_staff_name}</span>
                              <span>{formatDate(mistake.created_at)}</span>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 border-red-200 hover:bg-red-50 dark:text-red-400 dark:border-red-800 dark:hover:bg-red-900/20"
                              onClick={() => handleApproval(mistake.id, false)}
                              disabled={approvingId === mistake.id}
                            >
                              {approvingId === mistake.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <XCircle className="w-4 h-4 mr-1" />
                                  Reject
                                </>
                              )}
                            </Button>
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 text-white"
                              onClick={() => handleApproval(mistake.id, true)}
                              disabled={approvingId === mistake.id}
                            >
                              {approvingId === mistake.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  Approve
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </motion.div>
          )}
        </AnimatePresence>
      </Card>

      {/* By Staff Breakdown */}
      {report && (
        <Card>
          <CardHeader 
            className="cursor-pointer" 
            onClick={() => toggleSection('staff')}
          >
            <CardTitle className="text-lg flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                By Staff Member
              </span>
              {expandedSection === 'staff' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </CardTitle>
            <CardDescription>Staff with most mistakes (for performance reviews)</CardDescription>
          </CardHeader>
          <AnimatePresence>
            {expandedSection === 'staff' && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
              >
                <CardContent>
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {report.by_staff.length === 0 ? (
                      <p className="text-center text-muted-foreground py-4">No staff data</p>
                    ) : (
                      report.by_staff.map((staff, idx) => (
                        <div key={staff.staff_id} className={`flex justify-between items-center p-3 rounded-lg ${
                          idx === 0 ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800' : 'bg-muted'
                        }`}>
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                              idx === 0 ? 'bg-red-200 text-red-700 dark:bg-red-800 dark:text-red-200' : 'bg-gray-200 dark:bg-gray-700'
                            }`}>
                              {idx + 1}
                            </div>
                            <div>
                              <p className="font-medium text-foreground">{staff.staff_name}</p>
                              <p className="text-xs text-muted-foreground">{staff.staff_role}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-lg">{staff.incident_count} incidents</p>
                            <p className="text-sm text-muted-foreground">
                              Items: ${formatCurrency(staff.items_value)} | 
                              <span className="text-amber-600 dark:text-amber-400 ml-1">
                                Charged: ${formatCurrency(staff.staff_charged)}
                              </span>
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </motion.div>
            )}
          </AnimatePresence>
        </Card>
      )}

      {/* By Mistake Type */}
      {report && (
        <Card>
          <CardHeader 
            className="cursor-pointer" 
            onClick={() => toggleSection('types')}
          >
            <CardTitle className="text-lg flex items-center justify-between">
              <span className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                By Mistake Type
              </span>
              {expandedSection === 'types' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </CardTitle>
          </CardHeader>
          <AnimatePresence>
            {expandedSection === 'types' && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
              >
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {report.by_type.map((type) => (
                      <div key={type.type} className="p-3 bg-muted rounded-lg text-center">
                        <p className="text-2xl font-bold">{type.count}</p>
                        <p className="text-sm font-medium text-foreground">{type.type_name}</p>
                        <p className="text-xs text-red-600 dark:text-red-400">
                          ${formatCurrency(type.business_loss)} loss
                        </p>
                      </div>
                    ))}
                    {report.by_type.length === 0 && (
                      <p className="col-span-4 text-center text-muted-foreground py-4">No mistake type data</p>
                    )}
                  </div>
                </CardContent>
              </motion.div>
            )}
          </AnimatePresence>
        </Card>
      )}

      {/* By Day Pattern */}
      {report && (
        <Card>
          <CardHeader 
            className="cursor-pointer" 
            onClick={() => toggleSection('days')}
          >
            <CardTitle className="text-lg flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Mistake Patterns by Day
              </span>
              {expandedSection === 'days' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </CardTitle>
            <CardDescription>Identify high-risk days for additional training</CardDescription>
          </CardHeader>
          <AnimatePresence>
            {expandedSection === 'days' && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
              >
                <CardContent>
                  <div className="grid grid-cols-7 gap-2">
                    {report.by_day.map((day) => {
                      const maxCount = Math.max(...report.by_day.map(d => d.count), 1);
                      const intensity = day.count / maxCount;
                      return (
                        <div 
                          key={day.day} 
                          className={`p-3 rounded-lg text-center ${
                            intensity > 0.7 ? 'bg-red-100 dark:bg-red-900/30' :
                            intensity > 0.3 ? 'bg-amber-100 dark:bg-amber-900/30' :
                            'bg-green-100 dark:bg-green-900/30'
                          }`}
                        >
                          <p className="text-xs font-medium text-muted-foreground">{day.day.slice(0, 3)}</p>
                          <p className="text-xl font-bold">{day.count}</p>
                          <p className="text-xs text-red-600 dark:text-red-400">${formatCurrency(day.loss)}</p>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </motion.div>
            )}
          </AnimatePresence>
        </Card>
      )}

      {/* Peak Hours */}
      {report && report.peak_hours && report.peak_hours.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Peak Mistake Hours
            </CardTitle>
            <CardDescription>Times when most mistakes occur</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {report.peak_hours.map(([hour, count]) => (
                <div key={hour} className="px-3 py-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                  <span className="font-medium">
                    {hour === 0 ? '12 AM' : hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour - 12} PM`}
                  </span>
                  <span className="text-muted-foreground ml-2">({count} incidents)</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {!report && !loading && (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No report data available. Click Refresh to load.
          </CardContent>
        </Card>
      )}
    </div>
  );
}
